import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Rating,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Container,
  Snackbar,
  Alert
} from '@mui/material';
import { makeRequest } from "../../../assets/functions_helper";
import { SendButton } from "../../../components/buttons";
import { CustomTextField } from "../../../components/textFields";

// Custom hook para obtener los QueryParams fijos
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function ActiveSurveyView() {
  const query = useQuery();
  const tablePublicId = query.get("table_public_id");

  const [loading, setLoading] = useState(true);
  const [surveyData, setSurveyData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [answers, setAnswers] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    if (!tablePublicId) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    // Usar la función para el fetch saltándose los redirects nativos y localStorage
    makeRequest(
      "GET",
      `surveys/active/${tablePublicId}`,
      null,
      "application/json",
      null,
      null,
      () => { } // Nullifier para el login-redirect
    )
      .then((res) => {
        setSurveyData(res);

        // Setup state map
        const initialAnswers = {};
        if (res.questions) {
          res.questions.forEach(q => {
            initialAnswers[q.question_id] = q.type === 'numeric_rating' ? null : "";
          });
        }
        setAnswers(initialAnswers);
      })
      .catch((err) => {
        // En caso de 404 u otros errores (No hay activa o expiró)
        console.error("Survey Fetch Error:", err);
        setNotFound(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tablePublicId]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = () => {
    // Validación: que todas las preguntas tengan respuesta
    if (!surveyData || !surveyData.questions) return;

    for (let q of surveyData.questions) {
      const val = answers[q.question_id];
      if (val === null || val === undefined || (typeof val === 'string' && val.trim() === '')) {
        showSnackbar("Por favor, responde todas las preguntas antes de enviar.", "warning");
        return;
      }
    }

    const sessionId = localStorage.getItem("session_table");
    if (!sessionId) {
      showSnackbar("Error de sesión. No se pudo identificar tu sesión.", "error");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      survey_id: surveyData.survey_id,
      session_token: sessionId,
      answers: Object.keys(answers).map(key => ({
        question_id: key,
        value: answers[key]
      }))
    };

    makeRequest(
      "POST",
      "surveys/answer",
      payload,
      "application/json",
      null,
      null,
      () => { }
    )
      .then(() => {
        showSnackbar("¡Gracias por tus comentarios!", "success");
        setAnswers({});
        setIsSubmitted(true);
      })
      .catch((err) => {
        console.error("Error submitting survey:", err);
        showSnackbar("Hubo un problema al enviar tus respuestas. Por favor, intenta de nuevo.", "error");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F9FAFB' }}>
        <CircularProgress color="primary" />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Cargando...</Typography>
      </Box>
    );
  }

  // Flujo 404 o ya enviada:
  if (notFound || isSubmitted) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', justifyContent: 'center', alignItems: 'center', backgroundColor: '#F9FAFB', p: 3, textAlign: 'center' }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 4, backgroundColor: 'white', maxWidth: 400, width: '100%' }}>
          <Box sx={{ fontSize: 60, mb: 1 }}>🎉</Box>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1, color: '#1A1A1A' }}>
            ¡Gracias por tu visita!
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Tu opinión es muy importante para nosotros. ¡Esperamos verte pronto!
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Flujo Normal:
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F9FAFB', py: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}>
      <Container maxWidth="sm" sx={{ px: 0 }}>

        {/* Cabecera */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 900, color: '#1A1A1A', mb: 1 }}>
            {surveyData?.name || "Comparte tu Opinión"}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', px: 2 }}>
            {surveyData?.description || "Enséñanos cómo mejorar para brindarte siempre el mejor servicio."}
          </Typography>
        </Box>

        {/* Cuestionario Interactivo Iterate */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {surveyData?.questions?.map((q, index) => {
            const questionValue = answers[q.question_id];

            return (
              <Paper key={q.question_id} variant="outlined" sx={{ p: 3, borderRadius: 3, borderColor: '#e5e7eb', backgroundColor: 'white' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
                  {index + 1}. {q.text}
                </Typography>

                {/* Tipo Rating + Estrellas */}
                {q.type === 'numeric_rating' && q.display_type === 'stars' && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <Rating
                      size="large"
                      value={questionValue || 0}
                      onChange={(_, newValue) => handleAnswerChange(q.question_id, newValue)}
                      sx={{ color: '#FFB400', "& .MuiRating-iconEmpty": { color: "#e0e0e0" } }} // Amarillo vistoso clásico de app movil
                    />
                  </Box>
                )}

                {/* Tipo Rating + Numeros (1-5) */}
                {q.type === 'numeric_rating' && q.display_type === 'numeric' && (
                  <ToggleButtonGroup
                    value={questionValue}
                    exclusive
                    onChange={(_, newValue) => {
                      if (newValue !== null) handleAnswerChange(q.question_id, newValue);
                    }}
                    aria-label="numeric rating"
                    sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <ToggleButton
                        key={num}
                        value={num}
                        sx={{
                          flex: 1,
                          mx: 0.5,
                          borderRadius: '12px !important',
                          border: '1px solid #d1d5db !important',
                          fontWeight: 'bold',
                          "&.Mui-selected": {
                            backgroundColor: '#111827 !important', // Boton negro estilo premium
                            color: 'white !important'
                          }
                        }}
                      >
                        {num}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}

                {/* Tipo Respuesta Libre */}
                {q.type === 'open_ended' && (
                  <CustomTextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Escribe tus comentarios aquí..."
                    value={questionValue || ""}
                    onChange={(e) => handleAnswerChange(q.question_id, e.target.value)}
                    sx={{ m: 0 }}
                  />
                )}
              </Paper>
            );
          })}
        </Box>

        {/* Boton de Submit */}
        <Box sx={{ mt: 5, mb: 4, display: 'flex', justifyContent: 'center' }}>
          <SendButton
            fullWidth
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{ py: 1.5, fontSize: '1rem', borderRadius: 3 }}
          >
            {isSubmitting ? "Enviando..." : "Enviar Respuestas"}
          </SendButton>
        </Box>

      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
